import { getScimConfig } from '../utils/envUtils';
import { ScimAuthService } from './ScimAuthService';
import { debugVerboseLog } from '../utils/debugLog';

/**
 * Service for making CORS-enabled API calls through our backend proxy
 */
export class CorsApiService {
    private static instance: CorsApiService;
    private baseUrl: string;

    private constructor() {
        // Use the same base URL as the SCIM API, then append /cors
        // The controller route is [Route("[controller]")] which expands to "Cors"
        // So we just need to append "cors" to the base URL
        const scimConfig = getScimConfig();
        // Ensure we have a trailing slash from the apiUrl, then append cors
        const apiBase = scimConfig.apiUrl.endsWith('/')
            ? scimConfig.apiUrl.slice(0, -1)
            : scimConfig.apiUrl;
        // apiBase is already /api/scim/v2, and controller route is [controller], so we need cors
        this.baseUrl = `${apiBase}/cors`;
        debugVerboseLog(`🔧 CorsApiService initialized with baseURL=${this.baseUrl}`);
    }

    /**
     * Get authentication token for API calls
     */
    private async getAuthToken(): Promise<string> {
        return ScimAuthService.getAuthToken();
    }

    static getInstance(): CorsApiService {
        if (!CorsApiService.instance) {
            CorsApiService.instance = new CorsApiService();
        }
        return CorsApiService.instance;
    }

    /**
     * Geocode an address using the backend util/geocode endpoint
     */
    async geocodeAddress(address: string, city: string, state: string = 'FL', zip: string = '', country: string = 'us'): Promise<GeocodeResponse | null> {
        try {
            const params = new URLSearchParams({
                address,
                city,
                state,
                zip,
                country
            });

            // Use the new util/geocode endpoint
            const scimConfig = getScimConfig();
            const apiBase = scimConfig.apiUrl.endsWith('/')
                ? scimConfig.apiUrl.slice(0, -1)
                : scimConfig.apiUrl;
            const geocodeUrl = `${apiBase}/util/geocode?${params}`;

            const token = await this.getAuthToken();
            const response = await fetch(geocodeUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                // Try to extract error message from response
                let errorMessage = `Geocoding failed: ${response.status} ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (errorData.error) {
                        errorMessage = errorData.error;
                    } else if (typeof errorData === 'string') {
                        errorMessage = errorData;
                    }
                } catch {
                    // If we can't parse the error response, use the status text
                }
                console.error(errorMessage);
                throw new Error(errorMessage);
            }

            const data = await response.json();

            // Handle Nominatim response shape: lat/lon are strings, no address property
            // The controller should return: { lat: number, lon: number, display_name: string, address: string }
            // But if it returns the raw Nominatim response, we need to parse it
            if (Array.isArray(data) && data.length > 0) {
                // Raw Nominatim response - parse it
                const firstResult = data[0];
                const lat = typeof firstResult.lat === 'string'
                    ? parseFloat(firstResult.lat)
                    : (firstResult.lat as number);
                const lon = typeof firstResult.lon === 'string'
                    ? parseFloat(firstResult.lon)
                    : (firstResult.lon as number);

                return {
                    lat: lat,
                    lon: lon,
                    display_name: firstResult.display_name || '',
                    address: JSON.stringify(firstResult) // Full JSON object as string
                } as GeocodeResponse;
            } else if (data.lat !== undefined && data.lon !== undefined) {
                // Controller already parsed it - use as-is
                return data as GeocodeResponse;
            } else {
                throw new Error('Invalid geocoding response format');
            }

        } catch (error) {
            console.error('Error during geocoding:', error);
            // Re-throw the error so it can be handled by the caller
            if (error instanceof Error) {
                throw error;
            }
            throw new Error('Geocoding failed: Unknown error occurred');
        }
    }

    /**
     * Geocode a single free-form address string (e.g. "123 Main St, Jacksonville, FL 32256").
     * Uses the same util/geocode endpoint with the full string in the address parameter.
     */
    async geocodeByQuery(query: string): Promise<GeocodeResponse | null> {
        const trimmed = (query || '').trim();
        if (!trimmed) return null;
        return this.geocodeAddress(trimmed, '', '', '');
    }

    /**
     * Generic proxy request for any external API
     */
    async proxyRequest(request: ProxyRequest): Promise<ProxyResponse | null> {
        try {
            const token = await this.getAuthToken();
            const response = await fetch(`${this.baseUrl}/proxy`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(request)
            });

            if (!response.ok) {
                console.error(`Proxy request failed: ${response.status} ${response.statusText}`);
                return null;
            }

            const data = await response.json();
            return data as ProxyResponse;

        } catch (error) {
            console.error('Error during proxy request:', error);
            return null;
        }
    }

    /**
     * Health check for the Util controller
     */
    async healthCheck(): Promise<{ status: string; timestamp: string } | null> {
        try {
            const scimConfig = getScimConfig();
            const apiBase = scimConfig.apiUrl.endsWith('/')
                ? scimConfig.apiUrl.slice(0, -1)
                : scimConfig.apiUrl;
            const healthUrl = `${apiBase}/util/health`;

            const token = await this.getAuthToken();
            const response = await fetch(healthUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Health check failed: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            return data as { status: string; timestamp: string };

        } catch (error) {
            console.error('Util health check failed:', error);
            throw error;
        }
    }

    /**
     * Test endpoint for the Util controller
     */
    async testEndpoint(): Promise<{ message: string; timestamp: string; endpoints: string[] } | null> {
        try {
            const scimConfig = getScimConfig();
            const apiBase = scimConfig.apiUrl.endsWith('/')
                ? scimConfig.apiUrl.slice(0, -1)
                : scimConfig.apiUrl;
            // Different deployments have exposed this test endpoint at different routes.
            // Try the more consistent "util/*" shape first, then fall back to "/test".
            const primaryTestUrl = `${apiBase}/util/test`;
            const fallbackTestUrl = `${apiBase}/test`;

            const token = await this.getAuthToken();
            const fetchTest = async (url: string) =>
                fetch(url, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                });

            let response = await fetchTest(primaryTestUrl);
            if (response.status === 404) {
                response = await fetchTest(fallbackTestUrl);
            }

            if (!response.ok) {
                throw new Error(`Test endpoint failed: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            return data as { message: string; timestamp: string; endpoints: string[] };

        } catch (error) {
            console.error('Util test endpoint failed:', error);
            throw error;
        }
    }
}

/**
 * Response from geocoding API
 * The controller parses Nominatim's response where lat/lon are strings
 * and converts them to numbers, and includes the full JSON as address
 */
export interface GeocodeResponse {
    lat: number;  // Parsed from string by controller
    lon: number;  // Parsed from string by controller
    display_name: string;
    address: string;  // Full JSON object as string from Nominatim response
}

/**
 * Request for proxy API
 */
export interface ProxyRequest {
    url: string;
    method?: string;
    headers?: Record<string, string>;
    body?: string;
}

/**
 * Response from proxy API
 */
export interface ProxyResponse {
    statusCode: number;
    headers: Record<string, string>;
    content: string;
}

// Export singleton instance
export const corsApiService = CorsApiService.getInstance();
